import React, { createContext, useContext, useEffect, useState } from "react";
import { AuthContext } from "./AuthContext";
import { getCommitteeInRealTime } from "../Config/db/committee.operations";
import { getPermissionConfig, isActionEnabled, getActionType as getActionTypeFromConfig } from "../Config/db/permission.operations";

export const CommitteeContext = createContext();

/**
 * Provides committee membership info AND community permission config.
 *
 * Value shape:
 *  - committeeMembers: full list of committee assignments
 *  - myRoles: array of role objects the current user holds
 *  - isCommitteeMember: boolean — is the current user in the committee?
 *  - hasArea(area): does the current user own this permission area?
 *  - permConfig: Firestore-backed permission toggles for this community
 *  - isFeatureActionEnabled(actionId): checks Firestore config
 *  - loading: true while initial data is loading
 */
export const CommitteeContextProvider = ({ children }) => {
  const { userInfo, currentUser } = useContext(AuthContext);
  const [committeeMembers, setCommitteeMembers] = useState([]);
  const [permConfig, setPermConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userInfo?.communityCode) {
      setCommitteeMembers([]);
      setPermConfig(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    let committeeLoaded = false;
    let permLoaded = false;
    const checkDone = () => {
      if (committeeLoaded && permLoaded) setLoading(false);
    };

    const unsub1 = getCommitteeInRealTime(userInfo.communityCode, (members) => {
      setCommitteeMembers(members);
      committeeLoaded = true;
      checkDone();
    });

    const unsub2 = getPermissionConfig(userInfo.communityCode, (config) => {
      setPermConfig(config);
      permLoaded = true;
      checkDone();
    });

    return () => {
      unsub1 && unsub1();
      unsub2 && unsub2();
    };
  }, [userInfo?.communityCode]);

  const uid = currentUser?.uid;

  /** Roles the current user holds */
  const myRoles = committeeMembers.filter((m) => m.userId === uid);

  /** Is the current user on the committee? */
  const isCommitteeMember = myRoles.length > 0;

  /**
   * Does the current user own a specific permission area?
   * Returns true if ANY of the user's roles list that area.
   */
  const hasArea = (area) => {
    return myRoles.some((r) => (r.areas || []).includes(area));
  };

  /**
   * Does the current user have a specific action-level permission?
   * If `allowedActions` is defined on a role doc, uses it (granular mode).
   * Otherwise, falls back to area-level check (backward compat).
   */
  const hasAction = (actionId) => {
    return myRoles.some((r) => {
      // Granular mode: allowedActions array exists on the doc
      if (Array.isArray(r.allowedActions)) {
        return r.allowedActions.includes(actionId);
      }
      // Fallback: area-level (all actions in owned areas are allowed)
      return true; // handled by hasArea in the caller
    });
  };

  /** Get the member record for a given role key */
  const getMemberByRole = (role) =>
    committeeMembers.find((m) => m.role === role);

  /** Check if a given userId is on the committee */
  const isMember = (userId) =>
    committeeMembers.some((m) => m.userId === userId);

  /** Check if an action is enabled at the community level (Firestore config) */
  const isFeatureActionEnabled = (actionId) =>
    isActionEnabled(permConfig, actionId);

  /** Get the effective type for an action (Firestore override → defaultType) */
  const getActionType = (actionId) =>
    getActionTypeFromConfig(permConfig, actionId);

  return (
    <CommitteeContext.Provider
      value={{
        committeeMembers,
        myRoles,
        isCommitteeMember,
        hasArea,
        hasAction,
        getMemberByRole,
        isMember,
        isFeatureActionEnabled,
        getActionType,
        permConfig,
        loading,
      }}
    >
      {children}
    </CommitteeContext.Provider>
  );
};
